import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { message } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { INSERT_IMAGE_COMMAND } from 'features/richText'
import React, { useEffect } from 'react'

interface DragDropPastePluginProps {
  entryIdForImages?: string
}

const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
]
const MAX_FILE_SIZE_MB = 10

export const DragDropPastePlugin: React.FC<DragDropPastePluginProps> = ({
  entryIdForImages,
}) => {
  const [editor] = useLexicalComposerContext()
  const { rolodexUploadImage } = useRolodexEdit()

  useEffect(() => {
    if (!entryIdForImages) return

    const validateFile = (file: File): boolean => {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        message.error(
          `File type not accepted. Please upload: ${ACCEPTED_FILE_TYPES.map((t) => t.split('/')[1]).join(', ')}`
        )
        return false
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        message.error(`File must be smaller than ${MAX_FILE_SIZE_MB}MB`)
        return false
      }

      return true
    }

    const uploadAndInsertImage = async (file: File) => {
      if (!validateFile(file)) {
        return
      }

      const timestamp = Date.now()
      const sanitizedFileName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '-')
        .toLowerCase()
      const imagePath = `images/${timestamp}-${sanitizedFileName}`

      const hideLoading = message.loading('Uploading image...', 0)

      try {
        await rolodexUploadImage(entryIdForImages, file, imagePath)

        hideLoading()
        message.success('Image uploaded successfully')

        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          entryId: entryIdForImages,
          imagePath,
          altText: file.name,
        })
      } catch (error) {
        hideLoading()
        console.error('Upload failed:', error)
        message.error('Failed to upload image')
      }
    }

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy'
      }
    }

    const handleDrop = async (event: DragEvent) => {
      event.preventDefault()
      event.stopPropagation()

      const files = event.dataTransfer?.files
      if (!files || files.length === 0) return

      const file = Array.from(files).find((f) => f.type.startsWith('image/'))
      if (file) {
        await uploadAndInsertImage(file)
      }
    }

    const handlePaste = async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items
      if (!items) return

      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.startsWith('image/')) {
          event.preventDefault()
          event.stopPropagation()

          const file = item.getAsFile()
          if (file) {
            await uploadAndInsertImage(file)
          }
          break
        }
      }
    }

    const editorElement = editor.getRootElement()
    if (!editorElement) return

    editorElement.addEventListener('dragover', handleDragOver)
    editorElement.addEventListener('drop', handleDrop)
    editorElement.addEventListener('paste', handlePaste)

    return () => {
      editorElement.removeEventListener('dragover', handleDragOver)
      editorElement.removeEventListener('drop', handleDrop)
      editorElement.removeEventListener('paste', handlePaste)
    }
  }, [editor, entryIdForImages, rolodexUploadImage])

  return null
}
